"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { FolderPopulated } from "@vita-learning/types";
import { FOLDER_URL_SEGMENT, type FolderQueryParams, getFoldersKey, getFoldersUrl } from "~/shared/services/data/folders/query";
import { createFolder, deleteFolder, fetchFolders, getFolder, updateFolder } from "~/shared/services/data/folders/requests";

/**
 * Hook for fetching folders. If `id` is provided in the params, will fetch a single folder. Otherwise, will fetch all folders (optionally filtered by `folderId`).
 *
 * @param {FolderQueryParams} params the parameters for fetching folders
 */
export const useFolders = t0 => {
  const $ = _c(27);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const params = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== params) {
    t2 = getFoldersKey(params);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== params) {
    t3 = () => {
      if (params.disabled) {
        return [];
      }
      if (params.id) {
        return getFolder(params.id);
      }
      const url = getFoldersUrl(params);
      return fetchFolders(url);
    };
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const queryResults = useSuspenseQuery(t4);
  let t5;
  if ($[9] !== queryResults.data) {
    t5 = Array.isArray(queryResults.data) ? queryResults.data : [];
    $[9] = queryResults.data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const data = t5;
  const entity = !Array.isArray(queryResults.data) ? queryResults.data : null;
  let t6;
  if ($[11] !== queryClient) {
    t6 = () => queryClient.invalidateQueries({
      queryKey: [FOLDER_URL_SEGMENT]
    });
    $[11] = queryClient;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const revalidateAll = t6;
  let t7;
  if ($[13] !== revalidateAll) {
    t7 = {
      mutationFn: createFolder,
      onSuccess: revalidateAll
    };
    $[13] = revalidateAll;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const create = useMutation(t7);
  let t8;
  if ($[15] !== revalidateAll) {
    t8 = {
      mutationFn: updateFolder,
      onSuccess: revalidateAll
    };
    $[15] = revalidateAll;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const update = useMutation(t8);
  let t9;
  if ($[17] !== revalidateAll) {
    t9 = {
      mutationFn: deleteFolder,
      onSuccess: revalidateAll
    };
    $[17] = revalidateAll;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const remove = useMutation(t9);
  let t10;
  if ($[19] !== create || $[20] !== data || $[21] !== entity || $[22] !== queryResults || $[23] !== remove || $[24] !== revalidateAll || $[25] !== update) {
    t10 = {
      ...queryResults,
      data,
      entity,
      create,
      update,
      remove,
      revalidateAll
    };
    $[19] = create;
    $[20] = data;
    $[21] = entity;
    $[22] = queryResults;
    $[23] = remove;
    $[24] = revalidateAll;
    $[25] = update;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};